import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useStore } from 'react-redux'

import {
  CanonicalPickerGroupContainer,
  SingleLevelPickerGroupContainer,
  TwoLevelPickerGroupContainer,
} from 'containers/aiSegmentBuilder/picker'
import useCanonicalPickers from 'hooks/useCanonicalPickers'
import useFlagsReady from 'hooks/useFlagsReady'
import type {
  PickerData,
  PickerSubmissionPayload,
} from 'slices/aiConversations/pickerTypes'
import {
  selectNextAvailablePickerId,
  setGroupDisabled,
} from 'slices/aiSegmentBuilder/picker'
import loadCanonicalPickerGroup, {
  isCanonicalPicker,
} from 'slices/aiSegmentBuilder/picker/canonical/loadCanonicalPickerGroup'

import {
  buildSubmissionPayload,
  cleanupPickersBeforeSubmit,
  loadPickerGroup,
  ServerLevelConfig,
  ServerPickerResponse,
  ServerSelection,
} from 'scenes/Segments/AISegmentBuilder/ReviewItems/hooks'

import {
  buildSingleLevelPayload,
  loadSingleLevelPickerGroup,
} from 'scenes/Segments/AISegmentBuilder/ReviewItems/singleLevelHooks'

import gatePickerData from './gatePickerData'

interface ThanxAIPickerV2Props {
  data: PickerData
  onSubmit: (payload: PickerSubmissionPayload) => void
  isSubmitted?: boolean
  messageId: string
}

/**
 * Convert a PickerLevelConfig to ServerLevelConfig.
 */
const convertLevelConfig = (
  level: PickerData['level1'],
  defaults?: { keyField?: string; valueField?: string }
): ServerLevelConfig => ({
  id: level.id,
  label: level.label,
  selectionType: level.selectionType,
  operatorOptions: level.operatorOptions ?? [],
  endpoint: level.endpoint,
  params: level.params ?? {},
  keyField: level.keyField ?? defaults?.keyField ?? 'id',
  valueField: level.valueField ?? defaults?.valueField ?? 'name',
  operator: level.operator,
  subtitle: level.subtitle,
  sources: level.sources,
  defaultSource: level.defaultSource,
})

/**
 * Convert PickerData to ServerPickerResponse for a two-level picker.
 * Requires data.level2 to be defined.
 */
const convertToTwoLevelResponse = (data: PickerData): ServerPickerResponse => {
  const level1 = convertLevelConfig(data.level1)
  const level2 = convertLevelConfig(data.level2!)

  const selections: ServerSelection[] = data.selections.map(sel => ({
    level_1: Array.isArray(sel.level1) ? sel.level1[0] : sel.level1,
    level_1_label: sel.level1Label,
    operator: sel.operator ?? data.level2!.operator ?? 'skip',
    level_2: sel.level2 ?? [],
  }))

  return {
    picker_id: data.pickerId,
    type: data.type,
    title: data.title || 'Review',
    subtitle: data.subtitle ?? '',
    level_1: level1,
    level_2: level2,
    selections,
  }
}

/**
 * Convert PickerData to ServerPickerResponse for a single-level picker.
 * No level_2 config needed.
 */
const convertToSingleLevelResponse = (
  data: PickerData
): ServerPickerResponse => {
  const level1 = convertLevelConfig(data.level1)

  const selections: ServerSelection[] = data.selections.map(sel => ({
    level_1: Array.isArray(sel.level1) ? sel.level1[0] : sel.level1,
    level_1_label: sel.level1Label,
    operator: 'skip',
    level_2: [],
  }))

  return {
    picker_id: data.pickerId,
    type: data.type,
    title: data.title || 'Review',
    subtitle: data.subtitle ?? '',
    level_1: level1,
    selections,
  }
}

type PickerKind = 'canonical' | 'singleLevel' | 'twoLevel'

/**
 * ThanxAIPickerV2 - Redux-based picker that routes between the canonical,
 * single-level and two-level picker components based on backend configuration.
 *
 * Lifecycle:
 * 1. Once flags are ready: loads the picker data into Redux and settles which picker it is
 * 2. Renders: The picker container for that kind
 * 3. On confirm: Builds submission payload from Redux state and calls onSubmit
 */
const ThanxAIPickerV2 = ({
  data,
  onSubmit,
  isSubmitted = false,
  messageId,
}: ThanxAIPickerV2Props) => {
  const dispatch = useDispatch()
  const store = useStore()
  const canonicalPickers = useCanonicalPickers()
  const flagsReady = useFlagsReady()

  const groupId = useMemo(() => `picker_${messageId}`, [messageId])

  const hasHydratedRef = useRef(false)
  const [pickerKind, setPickerKind] = useState<PickerKind | null>(null)

  // Hydrate once, with the flag settled: a picker loaded in one shape can't switch to the other
  useEffect(() => {
    if (hasHydratedRef.current || !flagsReady) return

    const nextPickerId = selectNextAvailablePickerId(store.getState())
    const gatedData = gatePickerData(data, canonicalPickers)

    if (isCanonicalPicker(gatedData)) {
      loadCanonicalPickerGroup(dispatch, groupId, gatedData, nextPickerId)
      setPickerKind('canonical')
    } else if (!gatedData.level2) {
      loadSingleLevelPickerGroup(
        dispatch,
        groupId,
        convertToSingleLevelResponse(gatedData),
        nextPickerId
      )
      setPickerKind('singleLevel')
    } else {
      loadPickerGroup(
        dispatch,
        groupId,
        convertToTwoLevelResponse(gatedData),
        nextPickerId
      )
      setPickerKind('twoLevel')
    }

    hasHydratedRef.current = true
  }, [canonicalPickers, data, dispatch, flagsReady, groupId, store])

  // Sync isSubmitted prop with Redux disabled state, including on a hydration that came after it
  useEffect(() => {
    if (pickerKind) {
      dispatch(setGroupDisabled({ groupId, disabled: isSubmitted }))
    }
  }, [dispatch, groupId, isSubmitted, pickerKind])

  // The flag-on legacy picker names its config, as a canonical one always does; thanx-ai doesn't route on it
  const submit = useCallback(
    (payload: PickerSubmissionPayload) =>
      onSubmit(canonicalPickers ? { ...payload, type: data.type } : payload),
    [canonicalPickers, data.type, onSubmit]
  )

  // Confirm handlers — separate for each picker type (SOLID ISP)
  const handleSingleLevelConfirm = useCallback(
    (confirmedGroupId: string) => {
      const payload = buildSingleLevelPayload(
        store.getState(),
        confirmedGroupId
      )
      submit(payload)
    },
    [store, submit]
  )

  const handleTwoLevelConfirm = useCallback(
    (confirmedGroupId: string) => {
      const currentState = store.getState()
      cleanupPickersBeforeSubmit(dispatch, currentState, confirmedGroupId)
      const freshState = store.getState()
      const payload = buildSubmissionPayload(freshState, confirmedGroupId)
      submit(payload)
    },
    [dispatch, store, submit]
  )

  switch (pickerKind) {
    case 'canonical':
      return (
        <CanonicalPickerGroupContainer
          groupId={groupId}
          pickerType={data.type}
          onSubmit={onSubmit}
        />
      )
    case 'singleLevel':
      return (
        <SingleLevelPickerGroupContainer
          groupId={groupId}
          hasHydrated={true}
          onConfirm={handleSingleLevelConfirm}
        />
      )
    case 'twoLevel':
      return (
        <TwoLevelPickerGroupContainer
          groupId={groupId}
          hasHydrated={true}
          onConfirm={handleTwoLevelConfirm}
        />
      )
    default:
      return null
  }
}

export default ThanxAIPickerV2
